import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupItem } from '@/components/ui/input-group';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPending(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setPending(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token,
          password,
          confirmPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to reset password');
        setPending(false);
        return;
      }

      setSuccess(true);
      // Redirect to sign-in after 3 seconds
      setTimeout(() => {
        navigate('/sign-in');
      }, 3000);
    } catch (err) {
      setError('An error occurred. Please try again.');
      setPending(false);
    }
  };

  if (!token) {
    return (
      <div className="h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-danger-surface-strong p-3">
              <XCircle className="h-8 w-8 text-danger-foreground" />
            </div>
          </div>
          <h2 className="text-center text-3xl font-medium text-content-heading">
            Invalid reset link
          </h2>
          <p className="mt-4 text-center text-content-secondary">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xs">
          <Link to="/forgot-password">
            <Button variant="primary" className="w-full">
              Request new reset link
            </Button>
          </Link>
          <div className="mt-4">
            <Link
              to="/sign-in"
              className="flex items-center justify-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-success-surface-strong p-3">
              <CheckCircle className="h-8 w-8 text-success-foreground" />
            </div>
          </div>
          <h2 className="text-center text-3xl font-medium text-content-heading">
            Password reset successful
          </h2>
          <p className="mt-4 text-center text-content-secondary">
            Your password has been reset successfully. You'll be redirected to sign in shortly.
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xs">
          <Link to="/sign-in">
            <Button variant="primary" className="w-full">
              Sign in now
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-medium text-content-heading">
          Create new password
        </h2>
        <p className="mt-2 text-center text-sm text-content-secondary">
          Enter your new password below. Make sure it's at least 8 characters and includes uppercase, lowercase, and numbers.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xs">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <InputGroup>
              <InputGroupItem
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                maxLength={100}
                inlineLabel="Password *"
                labelWidth="120px"
                placeholder="Enter new password"
              />
              <InputGroupItem
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                maxLength={100}
                inlineLabel="Confirm *"
                labelWidth="120px"
                placeholder="Confirm new password"
              />
            </InputGroup>
          </div>
          {error && (
            <div className="text-danger-text text-sm -mt-4">{error}</div>
          )}

          <div>
            <Button
              type="submit"
              disabled={pending}
              className="w-full"
              variant="primary"
            >
              {pending ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  Resetting...
                </>
              ) : (
                'Reset password'
              )}
            </Button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/sign-in"
            className="flex items-center justify-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

